import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, Image, Zap, ChevronRight, Search, X, Edit2, Gift } from 'lucide-react';
import SorteoConfigPanel from './SorteoConfigPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TurboMarketingPanel = () => {
  const [activeTab, setActiveTab] = useState('banners');
  const [banners, setBanners] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [form, setForm] = useState({ trigger_product_id: '', upsell_product_id: '', title: '', description: '' });
  const [searchTrigger, setSearchTrigger] = useState('');
  const [searchUpsell, setSearchUpsell] = useState('');
  const [showTriggerSearch, setShowTriggerSearch] = useState(false);
  const [showUpsellSearch, setShowUpsellSearch] = useState(false);

  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadBanners = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/turbo-marketing/banners`, { headers });
      if (res.ok) setBanners(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/products`);
      if (res.ok) setProducts(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    Promise.all([loadBanners(), loadProducts()]).finally(() => setLoading(false));
  }, [loadBanners, loadProducts]);

  const getProductName = (id) => {
    const p = products.find(p => p.id === id);
    return p?.name?.es || p?.nombre?.es || 'Producto desconocido';
  };

  const getProductPrice = (id) => {
    const p = products.find(p => p.id === id);
    return p?.price ?? 0;
  };

  const filteredTrigger = products.filter(p => {
    const name = (p.name?.es || p.nombre?.es || '').toLowerCase();
    return name.includes(searchTrigger.toLowerCase());
  });

  const filteredUpsell = products.filter(p => {
    const name = (p.name?.es || p.nombre?.es || '').toLowerCase();
    return name.includes(searchUpsell.toLowerCase());
  });

  const openCreate = () => {
    setEditingBanner(null);
    setForm({ trigger_product_id: '', upsell_product_id: '', title: '', description: '' });
    setSearchTrigger('');
    setSearchUpsell('');
    setShowForm(true);
  };

  const openEdit = (banner) => {
    setEditingBanner(banner);
    setForm({
      trigger_product_id: banner.trigger_product_id,
      upsell_product_id: banner.upsell_product_id,
      title: banner.title,
      description: banner.description,
    });
    setSearchTrigger('');
    setSearchUpsell('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.trigger_product_id || !form.upsell_product_id || !form.title) {
      toast.error('Completa producto trigger, producto upsell y título');
      return;
    }
    try {
      const url = editingBanner
        ? `${API_URL}/api/turbo-marketing/banners/${editingBanner.id}`
        : `${API_URL}/api/turbo-marketing/banners`;
      const res = await fetch(url, {
        method: editingBanner ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(editingBanner ? 'Banner actualizado' : 'Banner creado');
        setShowForm(false);
        loadBanners();
      } else {
        toast.error('Error al guardar');
      }
    } catch (e) { toast.error('Error de conexión'); }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/turbo-marketing/banners/${id}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('Banner eliminado'); loadBanners(); }
    } catch (e) { toast.error('Error'); }
  };

  const handleToggle = async (banner) => {
    try {
      await fetch(`${API_URL}/api/turbo-marketing/banners/${banner.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ is_active: !banner.is_active }),
      });
      loadBanners();
    } catch (e) { toast.error('Error'); }
  };

  const handleImageUpload = async (bannerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/turbo-marketing/banners/${bannerId}/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) { toast.success('Imagen subida'); loadBanners(); }
    } catch (e) { toast.error('Error al subir imagen'); }
  };

  const handleImageDelete = async (bannerId) => {
    try {
      const res = await fetch(`${API_URL}/api/turbo-marketing/banners/${bannerId}/image`, {
        method: 'DELETE', headers,
      });
      if (res.ok) { toast.success('Imagen eliminada'); loadBanners(); }
    } catch (e) { toast.error('Error'); }
  };

  const ProductSearchField = ({ label, value, search, setSearch, showList, setShowList, onSelect, filtered }) => (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      {value ? (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
          <span className="flex-1 text-sm font-medium">{getProductName(value)}</span>
          <span className="text-sm text-gray-500">{getProductPrice(value).toFixed(2)}€</span>
          <button onClick={() => onSelect('')} className="text-gray-400 hover:text-red-500">
            <X size={16} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Buscar producto..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setShowList(true); }}
              onFocus={() => setShowList(true)}
              className="pl-9"
              data-testid={`search-${label.toLowerCase().replace(/\s/g, '-')}`}
            />
          </div>
          {showList && filtered.length > 0 && (
            <div className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto bg-white border rounded-lg shadow-lg">
              {filtered.slice(0, 15).map(p => (
                <button
                  key={p.id}
                  onClick={() => { onSelect(p.id); setShowList(false); setSearch(''); }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 flex justify-between items-center text-sm border-b last:border-0"
                  data-testid={`product-option-${p.id}`}
                >
                  <span>{p.name?.es || p.nombre?.es}</span>
                  <span className="text-gray-400">{p.price?.toFixed(2)}€</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (loading) return <div className="p-8 text-center text-gray-500">Cargando...</div>;

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto" data-testid="turbo-marketing-panel">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
          <Zap size={20} className="text-white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Turbo Marketing</h2>
          <p className="text-sm text-gray-500">Upsell y captación de clientes</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 bg-gray-100 rounded-xl">
        <button
          onClick={() => setActiveTab('banners')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${activeTab === 'banners' ? 'bg-white shadow-sm text-orange-700' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-banners"
        >
          <Zap size={16} /> Banners Upsell
        </button>
        <button
          onClick={() => setActiveTab('sorteo')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${activeTab === 'sorteo' ? 'bg-white shadow-sm text-purple-700' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-sorteo"
        >
          <Gift size={16} /> Sorteo
        </button>
      </div>

      {activeTab === 'sorteo' ? (
        <SorteoConfigPanel />
      ) : (
      <>
      <div className="flex justify-end mb-4">
        <Button onClick={openCreate} className="gap-2 rounded-xl bg-orange-600 hover:bg-orange-700" data-testid="create-banner-btn">
          <Plus size={18} /> Nuevo Banner
        </Button>
      </div>

      {banners.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <Zap size={48} className="mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">Sin banners de upsell</h3>
            <p className="text-sm text-gray-400 mb-4">
              Crea tu primer banner para hacer upsell cuando el cliente añada un producto.
            </p>
            <Button onClick={openCreate} variant="outline" className="gap-2 rounded-xl">
              <Plus size={16} /> Crear primer banner
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {banners.map(banner => (
            <Card key={banner.id} className={`overflow-hidden transition-all ${!banner.is_active ? 'opacity-60' : ''}`} data-testid={`banner-card-${banner.id}`}>
              <CardContent className="p-0">
                <div className="flex gap-4 p-4">
                  {/* Image */}
                  <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100 shrink-0 relative group">
                    {banner.image_url ? (
                      <>
                        <img
                          src={banner.image_url.startsWith('/api') ? `${API_URL}${banner.image_url}` : banner.image_url}
                          alt="" className="w-full h-full object-cover"
                        />
                        <button
                          onClick={() => handleImageDelete(banner.id)}
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        >
                          <Trash2 size={16} className="text-white" />
                        </button>
                      </>
                    ) : (
                      <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors">
                        <Upload size={20} className="text-gray-400 mb-1" />
                        <span className="text-xs text-gray-400">Imagen</span>
                        <input type="file" accept="image/*" className="hidden"
                          onChange={(e) => e.target.files[0] && handleImageUpload(banner.id, e.target.files[0])}
                        />
                      </label>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-gray-800 truncate">{banner.title}</h3>
                      <Switch checked={banner.is_active} onCheckedChange={() => handleToggle(banner)} data-testid={`toggle-${banner.id}`} />
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{banner.description}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs">
                      <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                        Trigger: {getProductName(banner.trigger_product_id)}
                      </span>
                      <ChevronRight size={14} className="text-gray-300" />
                      <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700">
                        Upsell: {getProductName(banner.upsell_product_id)} ({getProductPrice(banner.upsell_product_id).toFixed(2)}€)
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(banner)} className="h-8 w-8 p-0" data-testid={`edit-${banner.id}`}>
                      <Edit2 size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(banner.id)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700" data-testid={`delete-${banner.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap size={20} className="text-orange-500" />
              {editingBanner ? 'Editar Banner' : 'Nuevo Banner de Upsell'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <ProductSearchField
              label="Producto Trigger (activa el banner)"
              value={form.trigger_product_id}
              search={searchTrigger}
              setSearch={setSearchTrigger}
              showList={showTriggerSearch}
              setShowList={setShowTriggerSearch}
              onSelect={(id) => setForm(f => ({ ...f, trigger_product_id: id }))}
              filtered={filteredTrigger}
            />
            <ProductSearchField
              label="Producto Upsell (la oferta)"
              value={form.upsell_product_id}
              search={searchUpsell}
              setSearch={setSearchUpsell}
              showList={showUpsellSearch}
              setShowList={setShowUpsellSearch}
              onSelect={(id) => setForm(f => ({ ...f, upsell_product_id: id }))}
              filtered={filteredUpsell}
            />
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Título del banner</label>
              <Input
                placeholder="Ej: Descubre la Experiencia Vermú"
                value={form.title}
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                data-testid="banner-title-input"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Descripción</label>
              <textarea
                placeholder="Ej: Vermú artesano + tabla de embutidos + olivas por solo 18€"
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                className="w-full min-h-[80px] rounded-lg border border-gray-200 p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                data-testid="banner-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={handleSave} className="bg-orange-600 hover:bg-orange-700" data-testid="save-banner-btn">
              {editingBanner ? 'Guardar cambios' : 'Crear Banner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TurboMarketingPanel;
